import { useState, useEffect, useCallback } from "react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const fmt = (v) => `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PaymentViewPage({ debts, months, api }) {
  const [viewTab, setViewTab] = useState("by-month");
  const [selectedMonth, setSelectedMonth] = useState("0");
  const [selectedDebt, setSelectedDebt] = useState("");
  const [monthData, setMonthData] = useState([]);
  const [debtData, setDebtData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchByMonth = useCallback(async (idx) => {
    setLoading(true);
    try { const res = await axios.get(`${api}/payment-view/month/${idx}`); setMonthData(res.data); }
    catch { setMonthData([]); }
    setLoading(false);
  }, [api]);

  const fetchByDebt = useCallback(async (name) => {
    setLoading(true);
    try { const res = await axios.get(`${api}/payment-view/debt/${encodeURIComponent(name)}`); setDebtData(res.data); }
    catch { setDebtData([]); }
    setLoading(false);
  }, [api]);

  useEffect(() => { if (viewTab === "by-month") fetchByMonth(Number(selectedMonth)); }, [selectedMonth, viewTab, fetchByMonth]);
  useEffect(() => { if (viewTab === "by-debt" && selectedDebt) fetchByDebt(selectedDebt); }, [selectedDebt, viewTab, fetchByDebt]);
  useEffect(() => { if (debts.length > 0 && !selectedDebt) setSelectedDebt(debts[0].name); }, [debts, selectedDebt]);

  const totalScheduled = monthData.reduce((s, d) => s + d.scheduled_min, 0);
  const totalPaid = monthData.reduce((s, d) => s + (d.is_future ? 0 : d.actual_paid), 0);
  const totalAfter = monthData.reduce((s, d) => s + d.balance_after, 0);
  const isFutureMonth = monthData.length > 0 && monthData[0]?.is_future;
  const isCurrentMonth = monthData.length > 0 && monthData[0]?.is_current;

  return (
    <div data-testid="payment-view-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight">Payment View</h1>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">Quick lookup — balances, scheduled & actual payments</p>
      </div>

      <Tabs value={viewTab} onValueChange={setViewTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger data-testid="tab-by-month" value="by-month" className="rounded-sm text-xs uppercase tracking-wider">View by Month</TabsTrigger>
          <TabsTrigger data-testid="tab-by-debt" value="by-debt" className="rounded-sm text-xs uppercase tracking-wider">View by Debt</TabsTrigger>
        </TabsList>

        <TabsContent value="by-month">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Select Month:</span>
              <div className="w-48">
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger data-testid="month-selector" className="rounded-sm"><SelectValue placeholder="Select month" /></SelectTrigger>
                  <SelectContent>{months.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {isCurrentMonth && <Badge className="border border-blue-300 text-blue-700 bg-blue-50 text-[10px] px-2 py-0.5 rounded-sm" variant="outline">CURRENT MONTH</Badge>}
              {isFutureMonth && <Badge className="border border-gray-300 text-gray-500 bg-gray-50 text-[10px] px-2 py-0.5 rounded-sm" variant="outline">FUTURE PROJECTION</Badge>}
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-32"><p className="text-gray-400 text-sm">Loading...</p></div>
            ) : monthData.length === 0 ? (
              <div className="border border-gray-200 rounded-sm p-12 text-center"><p className="text-gray-400 text-sm">No payment data for this month.</p></div>
            ) : (
              <div className="border border-gray-200 rounded-sm overflow-x-auto custom-scroll">
                <table className="spreadsheet-table w-full">
                  <thead>
                    <tr>
                      <th className="text-center w-8">#</th>
                      <th className="text-left">Debt</th>
                      <th className="text-right">Opening Bal</th>
                      <th className="text-right">Sched Min</th>
                      <th className="text-right">Actual Paid</th>
                      <th className="text-right">Balance After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthData.map((d, i) => (
                      <tr key={i} data-testid={`payment-row-${d.debt_name}`}>
                        <td className="text-center font-mono-data text-xs text-gray-400">{i + 1}</td>
                        <td className="font-medium text-gray-900 whitespace-nowrap">
                          {d.debt_name}
                          {d.budget_linked && <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block ml-1.5 align-middle" title="Linked from Budget" />}
                          {d.is_promo_expiry && <Badge className="ml-1.5 border border-amber-300 text-amber-600 bg-amber-50 text-[10px] px-1 py-0 rounded-sm font-mono-data" variant="outline">0% EXPIRES</Badge>}
                          {d.is_interest_running && <Badge className="ml-1.5 border border-orange-300 text-orange-600 bg-orange-50 text-[10px] px-1 py-0 rounded-sm font-mono-data" variant="outline">INT.</Badge>}
                        </td>
                        <td className="numeric">{fmt(d.current_balance)}</td>
                        <td className="numeric">{fmt(d.scheduled_min)}</td>
                        <td className={`numeric ${d.is_future ? "text-gray-300" : "text-blue-700"}`}>
                          {d.is_future ? "-" : d.actual_paid > 0 ? fmt(d.actual_paid) : "-"}
                        </td>
                        <td className="numeric font-semibold">{fmt(d.balance_after)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td></td>
                      <td className="text-xs uppercase tracking-wider text-gray-600">TOTALS</td>
                      <td className="numeric">-</td>
                      <td className="numeric">{fmt(totalScheduled)}</td>
                      <td className="numeric text-blue-700">{isFutureMonth ? "-" : fmt(totalPaid)}</td>
                      <td className="numeric">{fmt(totalAfter)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="by-debt">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Select Debt:</span>
              <div className="w-56">
                <Select value={selectedDebt} onValueChange={setSelectedDebt}>
                  <SelectTrigger data-testid="debt-selector" className="rounded-sm"><SelectValue placeholder="Select debt" /></SelectTrigger>
                  <SelectContent>{debts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-32"><p className="text-gray-400 text-sm">Loading...</p></div>
            ) : debtData.length === 0 ? (
              <div className="border border-gray-200 rounded-sm p-12 text-center"><p className="text-gray-400 text-sm">No data for this debt.</p></div>
            ) : (
              <div className="border border-gray-200 rounded-sm overflow-x-auto custom-scroll">
                <table className="spreadsheet-table w-full">
                  <thead>
                    <tr>
                      <th className="text-left">Month</th>
                      <th className="text-right">Opening Bal</th>
                      <th className="text-right">Sched Min</th>
                      <th className="text-right">Actual Paid</th>
                      <th className="text-right">Balance After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debtData.map((d, i) => (
                      <tr key={i} data-testid={`debt-view-row-${d.month_label}`}
                        className={`${d.is_current ? "bg-blue-50/50 border-l-2 border-blue-500" : d.is_promo_expiry ? "bg-amber-50" : d.is_interest_running && !d.is_future ? "bg-orange-50/30" : d.is_future ? "opacity-60" : ""}`}>
                        <td className="font-mono-data text-xs text-gray-600 whitespace-nowrap">
                          {d.month_label}
                          {d.is_current && <Badge className="ml-1.5 border border-blue-300 text-blue-700 bg-blue-50 text-[10px] px-1 py-0 rounded-sm" variant="outline">NOW</Badge>}
                          {d.is_promo_expiry && <Badge className="ml-1.5 border border-amber-300 text-amber-600 bg-amber-50 text-[10px] px-1 py-0 rounded-sm font-mono-data" variant="outline">0% EXPIRES</Badge>}
                          {d.budget_linked && <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block ml-1" title="Linked from Budget" />}
                        </td>
                        <td className="numeric">{fmt(d.current_balance)}</td>
                        <td className="numeric">{fmt(d.scheduled_min)}</td>
                        <td className={`numeric ${d.is_future ? "text-gray-300" : "text-blue-700"}`}>
                          {d.is_future ? "-" : d.actual_paid > 0 ? fmt(d.actual_paid) : "-"}
                        </td>
                        <td className="numeric font-semibold">{fmt(d.balance_after)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="text-xs uppercase tracking-wider text-gray-600">TOTALS</td>
                      <td></td>
                      <td className="numeric">{fmt(debtData.reduce((s, d) => s + d.scheduled_min, 0))}</td>
                      <td className="numeric text-blue-700">{fmt(debtData.filter(d => !d.is_future).reduce((s, d) => s + d.actual_paid, 0))}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
